import { createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import {
  createPersonalApiKey,
  createProductionApiKey,
  createDevelopmentApiKey,
  regenerateDevelopmentApiKey,
  getDevelopmentApiKey,
} from "../../thunks/apiKey/apiKey.thunks";

interface StateWithApiKey {
  apiKey: IApiKeyState;
}

export interface IApiKeyState {
  loading: boolean;
  success: boolean;
  personalSuccess: boolean;
  productionSuccess: boolean;
  developmentSuccess: boolean;
  regenerateSuccess: boolean;
  error: boolean;
  errorCode: string;
  errorMessage: string;
  isCookieExpired: boolean;
  devApiKey: string;
  devApiValidUntil: string;
}

const initialState: IApiKeyState = {
  loading: false,
  success: false,
  personalSuccess: false,
  productionSuccess: false,
  developmentSuccess: false,
  regenerateSuccess: false,
  error: false,
  errorCode: "",
  errorMessage: "",
  isCookieExpired: false,
  devApiKey: "",
  devApiValidUntil: "",
};

export const apiKeySlice = createSlice({
  name: "apiKey",
  initialState,
  reducers: {
    resetApiKeyState(state) {
      state.loading = false;
      state.success = false;
      state.personalSuccess = false;
      state.productionSuccess = false;
      state.developmentSuccess = false;
      state.regenerateSuccess = false;
      state.error = false;
      state.error = false;
      state.errorCode = "";
      state.errorMessage = "";
      state.devApiKey = "";
      state.devApiValidUntil = "";
    },
    setIsCookieExpired(state, action: { payload: boolean }) {
      state.isCookieExpired = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Personal API Key
    builder
      .addCase(createPersonalApiKey.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.personalSuccess = false;
        state.error = false;
        state.errorCode = "";
        state.errorMessage = "";
      })
      .addCase(createPersonalApiKey.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.personalSuccess = true;
      })
      .addCase(createPersonalApiKey.rejected, (state, { payload }) => {
        const errorCode = get(payload, "code", "").toString();
        const errorMessage = get(payload, "message", "").toString();
        state.loading = false;
        state.error = true;
        state.errorCode = errorCode;
        state.errorMessage = errorMessage;
        if (errorCode === "401") {
          state.isCookieExpired = true;
        }
      })
      // Production API Key
      .addCase(createProductionApiKey.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.productionSuccess = false;
        state.error = false;
        state.errorCode = "";
        state.errorMessage = "";
      })
      .addCase(createProductionApiKey.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.productionSuccess = true;
      })
      .addCase(createProductionApiKey.rejected, (state, { payload }) => {
        const errorCode = get(payload, "code", "").toString();
        const errorMessage = get(payload, "message", "").toString();
        state.loading = false;
        state.error = true;
        state.errorCode = errorCode;
        state.errorMessage = errorMessage;
        if (errorCode === "401") {
          state.isCookieExpired = true;
        }
      })
      // Development API Key
      .addCase(createDevelopmentApiKey.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.developmentSuccess = false;
        state.error = false;
        state.errorCode = "";
        state.errorMessage = "";
      })
      .addCase(createDevelopmentApiKey.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        state.developmentSuccess = true;
        state.devApiKey = get(payload, "api_key", "").toString();
        state.devApiValidUntil = get(payload, "valid_until", "").toString();
      })
      .addCase(createDevelopmentApiKey.rejected, (state, { payload }) => {
        const errorCode = get(payload, "code", "").toString();
        const errorMessage = get(payload, "message", "").toString();
        state.loading = false;
        state.error = true;
        state.errorCode = errorCode;
        state.errorMessage = errorMessage;
        if (errorCode === "401") {
          state.isCookieExpired = true;
        }
      })
      // Regenerate Development API Key
      .addCase(regenerateDevelopmentApiKey.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.regenerateSuccess = false;
        state.error = false;
        state.errorCode = "";
        state.errorMessage = "";
      })
      .addCase(regenerateDevelopmentApiKey.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        state.regenerateSuccess = true;
        state.devApiKey = get(payload, "api_key", "").toString();
        state.devApiValidUntil = get(payload, "valid_until", "").toString();
      })
      .addCase(regenerateDevelopmentApiKey.rejected, (state, { payload }) => {
        const errorCode = get(payload, "code", "").toString();
        const errorMessage = get(payload, "message", "").toString();
        state.loading = false;
        state.error = true;
        state.errorCode = errorCode;
        state.errorMessage = errorMessage;
        if (errorCode === "401") {
          state.isCookieExpired = true;
        }
      })
      // Get Development API Key
      .addCase(getDevelopmentApiKey.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = false;
        state.errorCode = "";
        state.errorMessage = "";
      })
      .addCase(getDevelopmentApiKey.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        state.devApiKey = get(payload, "api_key", "").toString();
        state.devApiValidUntil = get(payload, "valid_until", "").toString();
      })
      .addCase(getDevelopmentApiKey.rejected, (state, { payload }) => {
        const errorCode = get(payload, "code", "").toString();
        const errorMessage = get(payload, "message", "").toString();
        state.loading = false;
        state.error = true;
        state.errorCode = errorCode;
        state.errorMessage = errorMessage;
        if (errorCode === "401") {
          state.isCookieExpired = true;
        }
      });
  },
});

export const { resetApiKeyState, setIsCookieExpired } = apiKeySlice.actions;

// Selectors
export const selectApiKeyLoading = (state: StateWithApiKey) =>
  state.apiKey.loading;
export const selectApiKeySuccess = (state: StateWithApiKey) =>
  state.apiKey.success;
export const selectApiKeyPersonalSuccess = (state: StateWithApiKey) =>
  state.apiKey.personalSuccess;
export const selectApiKeyProductionSuccess = (state: StateWithApiKey) =>
  state.apiKey.productionSuccess;
export const selectApiKeyDevelopmentSuccess = (state: StateWithApiKey) =>
  state.apiKey.developmentSuccess;
export const selectApiKeyRegenerateSuccess = (state: StateWithApiKey) =>
  state.apiKey.regenerateSuccess;
export const selectApiKeyError = (state: StateWithApiKey) => state.apiKey.error;
export const selectApiKeyErrorMessage = (state: StateWithApiKey) =>
  state.apiKey.errorMessage;
export const selectApiKeyDevKey = (state: StateWithApiKey) =>
  state.apiKey.devApiKey;
export const selectApiKeyDevValidUntil = (state: StateWithApiKey) =>
  state.apiKey.devApiValidUntil;

export default apiKeySlice.reducer;
